/**
 * WebRTCManager
 *
 * Manages multiple peer connections for a mesh video call.
 * Works together with MeetingSignaling — the signaling layer passes
 * SDP offers/answers and ICE candidates here; this class does the
 * actual RTCPeerConnection work and fires track/state events back.
 *
 * Negotiation follows the "perfect negotiation" pattern: whoever joins last
 * offers to everyone already in the room, renegotiation is driven by
 * onnegotiationneeded (so camera / screen-share toggles actually reach peers),
 * and offer collisions are resolved by a deterministic polite/impolite role.
 *
 * Usage:
 *   const rtc = new WebRTCManager(signalingInstance, localStream);
 *   rtc.on('remoteStream', ({ connectionId, stream, userId, displayName, photoData }) => ...);
 *   rtc.on('peerLeft',     ({ connectionId }) => ...);
 *   rtc.on('stateChange',  ({ connectionId, state }) => ...);
 *
 *   // Media controls:
 *   rtc.setLocalStream(newStream);       // replace tracks after device switch
 *   rtc.replaceVideoTrack(newTrack);
 *   rtc.replaceAudioTrack(newTrack);
 *
 *   rtc.destroy();                       // close all peer connections
 */

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export class WebRTCManager {
  constructor(signaling, localStream) {
    this._sig = signaling;
    this._localStream = localStream;
    this._peers = new Map();          // connectionId → peer entry
    this._earlyCandidates = new Map(); // candidates that arrived before the peer entry existed
    this._listeners = {};

    this._bindSignalingEvents();
  }

  // ─── Signaling event binding ───────────────────────────────────────────────

  _bindSignalingEvents() {
    this._sig.on('roomPeers',          peers  => this.handleRoomPeers(peers));
    this._sig.on('peerJoined',         data   => this._onPeerJoined(data));
    this._sig.on('receiveOffer',       data   => this.handleOffer(data.from, data.sdp));
    this._sig.on('receiveAnswer',      data   => this.handleAnswer(data.from, data.sdp));
    this._sig.on('receiveIceCandidate',data   => this.handleIceCandidate(data.from, data.candidateJson));
    this._sig.on('peerLeft',           data   => this.handlePeerLeft(data.connectionId));
    this._sig.on('meetingEnded',       ()     => this._emit('meetingEnded'));
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * We just joined a room that already has participants, so we are the offerer
   * for each of them. Adding our transceivers fires onnegotiationneeded, which
   * is what actually sends the offer.
   */
  handleRoomPeers(peers) {
    peers.forEach(peer => {
      const entry = this._ensurePeer(peer.connectionId, peer);
      this._addLocalTransceivers(entry);
    });
  }

  /**
   * Someone joined after us, so they are the offerer and we will answer.
   * We deliberately do NOT create transceivers here: an answerer's own
   * transceivers must come from the remote offer's m-lines, otherwise
   * setRemoteDescription() creates fresh receive-only ones and our microphone
   * never makes it into the answer at all.
   */
  _onPeerJoined(data) {
    this._ensurePeer(data.connectionId, data);
  }

  /** Received an SDP offer from a remote peer. */
  async handleOffer(fromConnectionId, sdp) {
    const entry = this._ensurePeer(fromConnectionId);
    const pc = entry.pc;

    // Offer collision: both sides offered at once. The impolite peer ignores
    // the incoming offer and keeps its own; the polite one rolls back.
    const collision = entry.makingOffer || pc.signalingState !== 'stable';
    if (collision && !this._isPolite(fromConnectionId)) {
      entry.ignoreOffer = true;
      return;
    }
    entry.ignoreOffer = false;

    try {
      await pc.setRemoteDescription({ type: 'offer', sdp });
      // Attach our own mic/camera to the transceivers the offer just created,
      // so the answer advertises us as a sender and not receive-only.
      this._bindLocalTracks(entry);
      await pc.setLocalDescription();
      await this._sig.sendAnswer(fromConnectionId, pc.localDescription.sdp);
      await this._flushCandidates(entry);
    } catch (err) {
      console.error('WebRTC: failed to answer offer', err);
    }
  }

  /** Received an SDP answer from a remote peer. */
  async handleAnswer(fromConnectionId, sdp) {
    const entry = this._peers.get(fromConnectionId);
    if (!entry) return;
    // A late answer to an offer we already replaced would throw — drop it.
    if (entry.pc.signalingState !== 'have-local-offer') return;

    try {
      await entry.pc.setRemoteDescription({ type: 'answer', sdp });
      await this._flushCandidates(entry);
    } catch (err) {
      console.error('WebRTC: failed to apply answer', err);
    }
  }

  /** Received an ICE candidate from a remote peer. */
  async handleIceCandidate(fromConnectionId, candidateJson) {
    let candidate;
    try { candidate = JSON.parse(candidateJson); } catch { return; }

    const entry = this._peers.get(fromConnectionId);
    if (!entry) {
      const queued = this._earlyCandidates.get(fromConnectionId) ?? [];
      queued.push(candidate);
      this._earlyCandidates.set(fromConnectionId, queued);
      return;
    }

    // Candidates that arrive before the remote description is applied cannot be
    // added yet — hold them instead of dropping them, or the connection can
    // settle on a worse route (or fail outright on stricter networks).
    if (!entry.pc.remoteDescription?.type) {
      entry.pendingCandidates.push(candidate);
      return;
    }

    try {
      await entry.pc.addIceCandidate(candidate);
    } catch (err) {
      if (!entry.ignoreOffer) console.warn('WebRTC: failed to add ICE candidate', err);
    }
  }

  /** A peer disconnected. */
  handlePeerLeft(connectionId) {
    const entry = this._peers.get(connectionId);
    if (entry) {
      entry.pc.onnegotiationneeded = null;
      entry.pc.close();
      this._peers.delete(connectionId);
    }
    this._earlyCandidates.delete(connectionId);
    this._emit('peerLeft', { connectionId });
  }

  /** Replace the local stream for all active peer connections (e.g. screen share). */
  setLocalStream(stream) {
    this._localStream = stream;
    const audioTrack = stream?.getAudioTracks()[0] ?? null;
    const videoTrack = stream?.getVideoTracks()[0] ?? null;
    this.replaceAudioTrack(audioTrack);
    this.replaceVideoTrack(videoTrack);
  }

  /**
   * Swap the video track on every peer connection.
   * Pass null to stop sending video (releases the camera indicator).
   * Direction changes here fire onnegotiationneeded, which renegotiates so the
   * remote side actually starts/stops receiving.
   */
  replaceVideoTrack(newTrack) {
    for (const [, entry] of this._peers) {
      if (!entry.videoSender) continue;
      entry.videoSender.replaceTrack(newTrack).catch(() => {});
      this._setSenderDirection(entry, entry.videoSender, newTrack);
    }
  }

  /** Swap just the audio track. */
  replaceAudioTrack(newTrack) {
    for (const [, entry] of this._peers) {
      if (!entry.audioSender) continue;
      entry.audioSender.replaceTrack(newTrack).catch(() => {});
      this._setSenderDirection(entry, entry.audioSender, newTrack);
    }
  }

  /** Close all peer connections and clean up. */
  destroy() {
    for (const [, entry] of this._peers) {
      entry.pc.onnegotiationneeded = null;
      entry.pc.close();
    }
    this._peers.clear();
    this._earlyCandidates.clear();
    this._listeners = {};
  }

  // ─── Events ───────────────────────────────────────────────────────────────

  on(event, handler) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
    return this;
  }

  _emit(event, payload) {
    (this._listeners[event] ?? []).forEach(h => h(payload));
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  _ensurePeer(connectionId, meta = {}) {
    const existing = this._peers.get(connectionId);
    if (existing) {
      // Merge meta to handle the race where an offer arrives before peerJoined
      if (Object.keys(meta).length > 0) existing.meta = { ...existing.meta, ...meta };
      return existing;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const entry = {
      pc,
      meta: { ...meta },
      audioSender: null,
      videoSender: null,
      transceiversAdded: false,
      makingOffer: false,
      ignoreOffer: false,
      pendingCandidates: this._earlyCandidates.get(connectionId) ?? [],
    };
    this._earlyCandidates.delete(connectionId);
    this._peers.set(connectionId, entry);

    // Renegotiation driver — fires when transceivers are added or a direction
    // changes (camera on/off, screen share, ICE restart).
    pc.onnegotiationneeded = async () => {
      try {
        entry.makingOffer = true;
        await pc.setLocalDescription();
        await this._sig.sendOffer(connectionId, pc.localDescription.sdp);
      } catch (err) {
        console.error('WebRTC: failed to send offer', err);
      } finally {
        entry.makingOffer = false;
      }
    };

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this._sig.sendIceCandidate(connectionId, JSON.stringify(candidate)).catch(() => {});
      }
    };

    const remoteStream = new MediaStream();
    pc.ontrack = ({ track, streams }) => {
      // Prefer the stream from the event; fall back to manually built one
      const stream = streams?.[0] ?? remoteStream;
      if (!stream.getTracks().includes(track)) stream.addTrack(track);
      this._emit('remoteStream', {
        connectionId,
        stream,
        userId: entry.meta.userId,
        displayName: entry.meta.displayName,
        photoData: entry.meta.photoData,
      });
    };

    pc.onconnectionstatechange = () => {
      this._emit('stateChange', { connectionId, state: pc.connectionState });
      if (pc.connectionState === 'failed') pc.restartIce();
    };

    return entry;
  }

  /** Offerer side: publish our mic/camera, audio m-line first for a stable order. */
  _addLocalTransceivers(entry) {
    if (entry.transceiversAdded) return;
    entry.transceiversAdded = true;

    const { pc } = entry;
    const stream = this._localStream;
    const audioTrack = stream?.getAudioTracks()[0] ?? null;
    const videoTrack = stream?.getVideoTracks()[0] ?? null;
    const streams = stream ? [stream] : [];

    const audioTr = pc.addTransceiver(audioTrack ?? 'audio', {
      direction: audioTrack ? 'sendrecv' : 'recvonly',
      streams,
    });
    entry.audioSender = audioTr.sender;

    const videoTr = pc.addTransceiver(videoTrack ?? 'video', {
      direction: videoTrack ? 'sendrecv' : 'recvonly',
      streams,
    });
    entry.videoSender = videoTr.sender;
  }

  /** Answerer side: bind our tracks onto the transceivers the remote offer created. */
  _bindLocalTracks(entry) {
    const { pc } = entry;
    const stream = this._localStream;
    const audioTrack = stream?.getAudioTracks()[0] ?? null;
    const videoTrack = stream?.getVideoTracks()[0] ?? null;

    for (const tr of pc.getTransceivers()) {
      if (tr.stopped) continue;
      const kind = tr.receiver?.track?.kind ?? tr.sender?.track?.kind;

      if (kind === 'audio') {
        entry.audioSender = tr.sender;
        if (tr.sender.track !== audioTrack) tr.sender.replaceTrack(audioTrack).catch(() => {});
        this._setDirection(tr, audioTrack ? 'sendrecv' : 'recvonly');
      } else if (kind === 'video') {
        entry.videoSender = tr.sender;
        if (tr.sender.track !== videoTrack) tr.sender.replaceTrack(videoTrack).catch(() => {});
        this._setDirection(tr, videoTrack ? 'sendrecv' : 'recvonly');
      }
    }
    entry.transceiversAdded = true;
  }

  _setSenderDirection(entry, sender, track) {
    const tr = entry.pc.getTransceivers().find(t => t.sender === sender);
    if (tr) this._setDirection(tr, track ? 'sendrecv' : 'recvonly');
  }

  /** Only touch direction when it actually changes — avoids spurious renegotiation. */
  _setDirection(transceiver, direction) {
    if (transceiver.direction !== direction) transceiver.direction = direction;
  }

  async _flushCandidates(entry) {
    if (!entry.pendingCandidates.length) return;
    const queued = entry.pendingCandidates;
    entry.pendingCandidates = [];
    for (const candidate of queued) {
      try { await entry.pc.addIceCandidate(candidate); } catch { /* stale candidate */ }
    }
  }

  /** Deterministic tie-break so exactly one side yields on an offer collision. */
  _isPolite(remoteConnectionId) {
    const myId = this._sig.connectionId ?? '';
    return myId < remoteConnectionId;
  }
}
